import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Flex, Input, InputNumber, Modal, Segmented, Space, Tag, Typography, message, List, Tooltip } from 'antd';
import { AudioOutlined, CheckCircleOutlined, DeleteOutlined, EditOutlined, PictureOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import JsonEditor from '../../JsonEditor/JsonEditor';
import { createEmptyAILessonPromptingConfig, TAILessonPromptingConfig, TAILessonPromptingRenderType } from '../../../models/courses/aiLessonPrompting';
import './AILessonPromptingAdmin.css';
import { useObjectsApi } from '../../../api/objects/objects';
import type { TImage, TSound } from '../../../models/objects/media';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

type MediaKind = 'image' | 'audio';

interface AILessonPromptingAdminProps {
  config: TAILessonPromptingConfig;
  onConfigChange: (config: TAILessonPromptingConfig) => void;
  onSave: () => void;
  saving?: boolean;
  initialStatement?: string;
  onStatementChange?: (value: string) => void;
}

const AILessonPromptingAdmin: React.FC<AILessonPromptingAdminProps> = ({
  config,
  onConfigChange,
  onSave,
  saving = false,
  initialStatement,
  onStatementChange,
}) => {
  const { t } = useTranslation();
  const [keywordInput, setKeywordInput] = useState('');
  const [jsonOpen, setJsonOpen] = useState(false);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaKind, setMediaKind] = useState<MediaKind>('image');
  const [images, setImages] = useState<TImage[]>([]);
  const [sounds, setSounds] = useState<TSound[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const objectsApi = useObjectsApi();

  const updateConfig = (updater: (prev: TAILessonPromptingConfig) => TAILessonPromptingConfig) => {
    const next = updater(config);
    onConfigChange(next);
  };

  const handleAddKeyword = () => {
    const value = keywordInput.trim();
    if (!value) return;
    updateConfig((prev) => ({
      ...prev,
      keywords: [...(prev.keywords || []), value],
    }));
    setKeywordInput('');
  };

  const handleRemoveKeyword = (kw: string) => {
    updateConfig((prev) => {
      const newKeywords = (prev.keywords || []).filter((k) => k !== kw);
      const newKeywordsCount = newKeywords.length;
      const currentTexts = prev.answerTexts || [];
      
      // Trim answerTexts if it exceeds the new keyword count
      const trimmedTexts = currentTexts.slice(0, newKeywordsCount);
      
      return {
        ...prev,
        keywords: newKeywords,
        requiredKeywords: Math.min(prev.requiredKeywords || 0, Math.max(newKeywordsCount - 1, 0)),
        answerTexts: trimmedTexts,
      };
    });
  };

  const handleRenderTypeChange = (value: TAILessonPromptingRenderType) => {
    updateConfig((prev) => ({
      ...prev,
      renderType: value,
    }));
  };

  const handleAnswerChange = (value: string) => {
    if (config.renderType === 'text') {
      updateConfig((prev) => ({ ...prev, answerText: value }));
    } else if (config.renderType === 'image') {
      updateConfig((prev) => ({ ...prev, answerImage: value }));
    } else {
      updateConfig((prev) => ({ ...prev, answerAudio: value }));
    }
  };

  const handleTextsChange = (index: number, value: string) => {
    updateConfig((prev) => {
      const texts = [...(prev.answerTexts || [])];
      texts[index] = value;
      return { ...prev, answerTexts: texts };
    });
  };

  const handleAddText = () => {
    const currentTextsCount = (config.answerTexts || []).length;
    const keywordsCount = (config.keywords || []).length;
    
    // Prevent adding more texts than keywords
    if (currentTextsCount >= keywordsCount) {
      message.warning(
        t('aiPrompting.admin.maxTextsReached') || 
        `Vous ne pouvez pas ajouter plus de ${keywordsCount} texte${keywordsCount > 1 ? 's' : ''} (nombre de mots-clés).`
      );
      return;
    }
    
    updateConfig((prev) => ({
      ...prev,
      answerTexts: [...(prev.answerTexts || []), ''],
    }));
  };

  const handleRemoveText = (index: number) => {
    updateConfig((prev) => {
      const texts = [...(prev.answerTexts || [])];
      texts.splice(index, 1);
      return { ...prev, answerTexts: texts };
    });
  };

  const openMediaPicker = (kind: MediaKind) => {
    setMediaKind(kind);
    setMediaPickerOpen(true);
    setMediaLoading(true);
    const loader = kind === 'image' ? objectsApi.getImages() : objectsApi.getSounds();
    loader
      .then((resp) => {
        if (kind === 'image') setImages(resp.data || []);
        else setSounds(resp.data || []);
      })
      .catch((err: any) => {
        message.error(err?.response?.data || err?.message || 'Failed to load media');
      })
      .finally(() => setMediaLoading(false));
  };

  const chooseMedia = (filePath: string) => {
    handleAnswerChange(filePath);
    setMediaPickerOpen(false);
  };

  const resetConfig = () => {
    Modal.confirm({
      title: t('aiPrompting.admin.confirmResetTitle') || 'Réinitialiser la configuration',
      content: t('aiPrompting.admin.confirmResetContent') || 'Êtes-vous sûr ?',
      onOk: () => onConfigChange(createEmptyAILessonPromptingConfig()),
    });
  };

  const answerValue = useMemo(() => {
    switch (config.renderType) {
      case 'image':
        return config.answerImage || '';
      case 'audio':
        return config.answerAudio || '';
      default:
        return config.answerText || '';
    }
  }, [config]);

  useEffect(() => {
    if (config.requiredKeywords > (config.keywords?.length || 0)) {
      updateConfig((prev) => ({
        ...prev,
        requiredKeywords: prev.keywords?.length || 0,
      }));
    }
    // eslint-disable-next-line 
  }, [config.keywords?.length]);

  // Auto-trim answerTexts when keywords are reduced
  useEffect(() => {
    const keywordsCount = (config.keywords || []).length;
    const textsCount = (config.answerTexts || []).length;
    
    if (textsCount > keywordsCount && keywordsCount > 0) {
      updateConfig((prev) => ({
        ...prev,
        answerTexts: (prev.answerTexts || []).slice(0, keywordsCount),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.keywords?.length]);

  return (
    <div className="ai-prompting-admin">
      <Card
        title={<Title level={4}>{t('aiPrompting.admin.title') || 'Configuration AI Prompting'}</Title>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={resetConfig}>
              {t('aiPrompting.admin.reset') || 'Réinitialiser'}
            </Button>
            <Button icon={<SaveOutlined />} type="primary" loading={saving} onClick={onSave}>
              {t('aiPrompting.admin.save') || 'Sauvegarder'}
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Paragraph>
              {t('aiPrompting.admin.description') ||
                "Définissez la liste de mots-clés exigés dans la prompt, le type de rendu et la bonne réponse."}
            </Paragraph>
          </div>

          <div className="ai-prompting-section">
            <Title level={5}>{t('aiPrompting.admin.statement') || 'Énoncé'}</Title>
            <TextArea
              rows={3}
              value={initialStatement ?? config.statement}
              placeholder={t('aiPrompting.admin.statementPlaceholder') || 'Saisissez l’énoncé visible par l’élève'}
              onChange={(e) => {
                updateConfig((prev) => ({ ...prev, statement: e.target.value }));
                onStatementChange?.(e.target.value);
              }}
            />
          </div>

          <div className="ai-prompting-section">
            <Title level={5}>{t('aiPrompting.admin.renderType') || 'Type de rendu'}</Title>
            <Segmented
              value={config.renderType || 'text'}
              onChange={(val) => handleRenderTypeChange(val as TAILessonPromptingRenderType)}
              options={[
                { label: <span><EditOutlined /> {t('aiPrompting.admin.render.text') || 'Texte'}</span>, value: 'text' },
                { label: <span><PictureOutlined /> {t('aiPrompting.admin.render.image') || 'Image'}</span>, value: 'image' },
                { label: <span><AudioOutlined /> {t('aiPrompting.admin.render.audio') || 'Audio'}</span>, value: 'audio' },
              ]}
            />
          </div>

          <div className="ai-prompting-section">
            <Flex justify="space-between" align="center">
              <Title level={5}>{t('aiPrompting.admin.keywords') || 'Mots-clés requis'}</Title>
              <Space>
                <Input
                  placeholder={t('aiPrompting.admin.keywordPlaceholder') || 'Ajouter un mot-clé'}
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onPressEnter={handleAddKeyword}
                  style={{ width: 220 }}
                />
                <Button icon={<PlusOutlined />} type="primary" onClick={handleAddKeyword}>
                  {t('aiPrompting.admin.addKeyword') || 'Ajouter'}
                </Button>
              </Space>
            </Flex>
            <Space wrap>
              {(config.keywords || []).map((kw) => (
                <Tag
                  key={kw}
                  closable
                  onClose={() => handleRemoveKeyword(kw)}
                  closeIcon={<DeleteOutlined />}
                  color="blue"
                >
                  {kw}
                </Tag>
              ))}
              {(config.keywords || []).length === 0 && (
                <Text type="secondary">{t('aiPrompting.admin.noKeywords') || 'Aucun mot-clé pour le moment.'}</Text>
              )}
            </Space>
            <div style={{ marginTop: 12 }}>
              <Text strong>{t('aiPrompting.admin.requiredCount') || 'Nombre minimum de mots-clés'}</Text>
              <br />
              <InputNumber
                min={0}
                max={config.keywords?.length || 0}
                value={config.requiredKeywords || 0}
                onChange={(value) => updateConfig((prev) => ({ ...prev, requiredKeywords: value || 0 }))}
              />
              <Text type="secondary" style={{ marginLeft: 8 }}>
                {t('aiPrompting.admin.requiredHint') || 'Détermine quand le rendu devient clair/net.'}
              </Text>
            </div>
          </div>

          <div className="ai-prompting-section">
            <Title level={5}>{t('aiPrompting.admin.answer') || 'Bonne réponse'}</Title>
            {config.renderType === 'text' ? (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', marginBottom: 8, display: 'block' }}>
                    {t('aiPrompting.admin.textsDescription') || 
                      'Ajoutez des textes progressifs. Le premier texte s\'affiche avec 1 mot-clé trouvé, le deuxième avec 2 mots-clés, etc.'}
                  </Text>
                </div>
                {(config.answerTexts || []).map((text, index) => (
                  <Card
                    key={index}
                    size="small"
                    style={{ marginBottom: 8 }}
                    extra={
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveText(index)}
                        disabled={(config.answerTexts || []).length <= 1}
                      >
                        {t('aiPrompting.admin.remove') || 'Supprimer'}
                      </Button>
                    }
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      <Text strong>
                        {t('aiPrompting.admin.textForKeywords', { count: index + 1 }) || 
                          `Texte pour ${index + 1} mot-clé${index + 1 > 1 ? 's' : ''} trouvé${index + 1 > 1 ? 's' : ''}`}
                      </Text>
                      <TextArea
                        rows={3}
                        value={text}
                        placeholder={t('aiPrompting.admin.answerTextPlaceholder') || 'Saisissez la réponse texte attendue'}
                        onChange={(e) => handleTextsChange(index, e.target.value)}
                      />
                    </Space>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleAddText}
                  disabled={(config.answerTexts || []).length >= (config.keywords || []).length}
                  block
                >
                  {t('aiPrompting.admin.addText') || 'Ajouter un texte'}
                </Button>
                {(config.answerTexts || []).length === 0 && (
                  <Alert
                    type="info"
                    message={t('aiPrompting.admin.noTexts') || 'Aucun texte configuré. Ajoutez au moins un texte.'}
                    showIcon
                  />
                )}
              </Space>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Input
                  value={answerValue}
                  placeholder={t('aiPrompting.admin.answerMediaPlaceholder') || 'Sélectionnez un média ou collez son chemin'}
                  onChange={(e) => handleAnswerChange(e.target.value)}
                  addonAfter={
                    <Button
                      icon={config.renderType === 'image' ? <PictureOutlined /> : <AudioOutlined />}
                      onClick={() => openMediaPicker(config.renderType as MediaKind)}
                    >
                      {t('aiPrompting.admin.chooseMedia') || 'Choisir un média'}
                    </Button>
                  }
                />
                {config.renderType === 'image' && answerValue && (
                  <div className="ai-prompting-media-preview">
                    <img src={answerValue} alt="preview" />
                  </div>
                )}
                {config.renderType === 'audio' && answerValue && (
                  <audio src={answerValue} controls />
                )}
              </Space>
            )}
          </div>

          <div className="ai-prompting-section">
            <Space>
              <Button icon={<EditOutlined />} onClick={() => setJsonOpen(true)}>
                {t('aiPrompting.admin.jsonEditor') || 'Éditeur JSON'}
              </Button>
            </Space>
          </div>
        </Space>
      </Card>

      <Modal
        open={jsonOpen}
        onCancel={() => setJsonOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <JsonEditor<TAILessonPromptingConfig>
          title={t('aiPrompting.admin.jsonTitle') || 'JSON de configuration'}
          value={config}
          onApply={(data) => {
            onConfigChange(data);
            onStatementChange?.(data.statement || '');
            message.success(t('aiPrompting.admin.jsonApplied') || 'Configuration appliquée');
            setJsonOpen(false);
          }}
          minRows={16}
          showStatement
          initialStatement={initialStatement ?? config.statement ?? ''}
          onStatementChange={(val) => {
            onStatementChange?.(val);
            updateConfig((prev) => ({ ...prev, statement: val }));
          }}
        />
      </Modal>

      <Modal
        open={mediaPickerOpen}
        onCancel={() => setMediaPickerOpen(false)}
        footer={null}
        width={700}
        title={
          mediaKind === 'image'
            ? t('aiPrompting.admin.mediaTitleImage') || 'Choisir une image'
            : t('aiPrompting.admin.mediaTitleAudio') || 'Choisir un audio'
        }
      >
        <List
          loading={mediaLoading}
          dataSource={mediaKind === 'image' ? images : sounds}
          rowKey={(item: TImage | TSound) => String(item.id)}
          renderItem={(item: any) => (
            <List.Item
              actions={[
                <Button type="link" onClick={() => chooseMedia(item.file_path)} key="select" icon={<CheckCircleOutlined />}>
                  {t('aiPrompting.admin.select') || 'Sélectionner'}
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  mediaKind === 'image' ? (
                    <div className="ai-prompting-media-thumb">
                      <img src={(item as TImage).file_path} alt={(item as TImage).file_name} />
                    </div>
                  ) : (
                    <AudioOutlined />
                  )
                }
                title={
                  <Tooltip title={item.file_name || item.file_path}>
                    <Text>{item.file_name || item.file_path}</Text>
                  </Tooltip>
                }
                description={item.file_path}
              />
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
};

export default AILessonPromptingAdmin;
